/**
 * Active-task toolbar — a fixed, full-width dark bar shown while I have a
 * running OR paused task. It is the single source of that task's actions
 * (Pause/Resume / Done — and +Part, withheld this release); the row
 * inside the WO card stays status-only. When paused, the primary button flips
 * to Resume and the dot
 * stops pulsing, so the task stays one tap from resuming. Tapping the bar
 * body (not a button) opens the WO detail.
 *
 * Fixed to the viewport bottom so it floats over the scrolling list — the
 * page content adds matching bottom padding so the last card never hides
 * behind it. The dark `bg-bg-elevated` surface reads as elevated in both
 * themes; the pulse dot + tabular timer echo the card's active row.
 *
 * +Part opens an anchored DropList popover (pick a catalog part) backed by
 * the mock task-scratch store, so it works offline; a teammate wires real
 * persistence later. Done is still visual for now. +Part and its popover
 * are gated off by FEATURE_TECH_PART_REQUESTS — technicians no longer put
 * parts on a job, they tell the manager.
 *
 * Used to also carry a Note quick-action. Removed 2026-09-22 — a mechanic
 * writes one note per job now, only inside the opened job (WoJobNotes),
 * not from a bar shortcut.
 */
import { useEffect, useRef, useState } from 'react'
import { ArrowRight, Check, Pause, Play, Plus, Timer } from '@phosphor-icons/react'
import { ButtonSpinner } from '@vollgas/shared/ui'
import { cn, formatDuration } from '@vollgas/shared/utils'
import { FAST_TICK, SLOW_TICK, useNow } from '@vollgas/shared/hooks'
import { useI18n } from '@/i18n/TranslationContext'
import { taskMinutes } from '@/lib/taskTime'
import { PartPickerPopover } from '@/components/PartPickerPopover'
import { usePartAddConfirm } from '@/components/usePartAddConfirm'
import { FEATURE_TECH_PART_REQUESTS } from '@/featureFlags'

interface BottomActionBarProps {
  readonly taskName: string
  /** "Audi A4 Avant · W-1834 LM" — vehicle + plate, never a customer name. */
  readonly vehicleLine: string
  readonly loggedMinutes: number
  readonly runningSince: string | null
  /** The active SERVICE — scopes the +Part quick action, and the timer.
   *  Parts hang off the service. */
  readonly serviceId: string
  /** The task's work order — the Part-request endpoint is WO-scoped. */
  readonly workOrderId: string
  readonly onOpen: () => void
  /** The shown task is paused → flip the primary button to Resume. */
  readonly paused: boolean
  readonly onPause: () => void
  readonly onResume: () => void
  /** A timer request is in flight — the spinner stands in for whichever icon
   *  the primary button is showing, so the icon returning is the confirmation.
   *  One flag, not one per direction: the optimistic patch flips `paused` on
   *  tap, which swaps the button before the request resolves. */
  readonly timerPending: boolean
  readonly onDone: () => void
  /** Animation/extra classes from the shell (show/hide slide). */
  readonly className?: string
}

const QUIET = 'inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2.5 text-13 font-bold text-white transition hover:bg-white/20'

export function BottomActionBar({
  taskName, vehicleLine, loggedMinutes, runningSince,
  serviceId, workOrderId, onOpen, paused, onPause, onResume, timerPending, onDone, className,
}: BottomActionBarProps) {
  const { t } = useI18n()
  // The bar only shows while a task is mine, so "not paused" is "running".
  const now = useNow(paused ? SLOW_TICK : FAST_TICK)
  const minutes = taskMinutes({ loggedMinutes, runningSince }, now)
  const act = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn() }

  // +Part opens an anchored DropList popover from its button.
  const partRef = useRef<HTMLButtonElement>(null)
  const [menu, setMenu] = useState<'part' | null>(null)
  const toggle = (which: 'part') => setMenu((m) => (m === which ? null : which))
  // Called unconditionally even while the +Part pill is withheld — it is a
  // hook, and moving it under the flag would make the hook order depend on a
  // build-time constant. It renders nothing until a pick comes in, and no
  // picker can reach it with the pill and popover gated out below.
  const { requestAdd, confirmNode } = usePartAddConfirm(workOrderId, serviceId)

  return (
    <>
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen() }}
      className={cn(
        'fixed inset-x-0 bottom-0 z-30 flex cursor-pointer items-center gap-5 border-t border-white/10 bg-bg-elevated px-6 py-4 text-white',
        className,
      )}
      style={{
        boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.25)',
        paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
      }}
    >
      <span className={cn('h-px12 w-px12 flex-shrink-0 rounded-full', paused ? 'bg-orange' : 'animate-pulse-dot bg-green')} aria-hidden="true" />
      <span className="flex flex-shrink-0 items-center gap-1.5 font-display text-18 font-extrabold tabular-nums tracking-mk-neg2">
        <Timer size={18} weight="bold" aria-hidden="true" /> {formatDuration(minutes, { days: true })}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-15 font-bold">{taskName}</div>
        <div className="truncate text-12-5 text-white/60">{vehicleLine}</div>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2.5">
        {paused ? (
          <button type="button" onClick={act(onResume)} className="inline-flex items-center gap-1.5 rounded-full bg-green px-4 py-2.5 text-13 font-bold text-white transition hover:opacity-90">
            {timerPending ? <ButtonSpinner /> : <Play size={15} weight="fill" aria-hidden="true" />} {t.tasks.resume}
          </button>
        ) : (
          <button type="button" onClick={act(onPause)} className="inline-flex items-center gap-1.5 rounded-full bg-orange px-4 py-2.5 text-13 font-bold text-white transition hover:opacity-90">
            {timerPending ? <ButtonSpinner /> : <Pause size={15} weight="fill" aria-hidden="true" />} {t.tasks.pause}
          </button>
        )}
        {/* +Part is withheld this release — a technician tells the manager in
            person and the manager adds it (see featureFlags.ts). */}
        {FEATURE_TECH_PART_REQUESTS && (
          <button ref={partRef} type="button" aria-expanded={menu === 'part'} onClick={act(() => toggle('part'))} className={cn(QUIET, menu === 'part' && 'bg-white/25')}>
            <Plus size={15} weight="bold" aria-hidden="true" /> {t.tasks.part}
          </button>
        )}
        <button type="button" onClick={act(onDone)} className="inline-flex items-center gap-1.5 rounded-full bg-green px-5 py-2.5 text-13 font-bold text-white transition hover:opacity-90 active:scale-[0.97]">
          <Check size={15} weight="bold" aria-hidden="true" /> {t.tasks.markDone}
        </button>
        <button type="button" onClick={act(onOpen)} className="inline-flex items-center gap-1 px-2 text-13 font-semibold text-white/60 transition hover:text-white">
          {t.home.openWO} <ArrowRight size={14} weight="bold" aria-hidden="true" />
        </button>
      </div>
    </div>

    {FEATURE_TECH_PART_REQUESTS && menu === 'part' && (
      <PartPickerPopover
        anchor={partRef.current}
        onPick={(part) => {
          requestAdd(part)
          setMenu(null)
        }}
        onClose={() => setMenu(null)}
      />
    )}
    {confirmNode}
    </>
  )
}
